import { Button as MuiButton, ButtonProps } from '@mui/material';
import { Button as FplButton, type ButtonProps as FplButtonProps } from '@figma/fpl-components';
import { forwardRef, type CSSProperties } from 'react';
import { color } from '@/colors';
import { FF_FIGMA_THEME } from '@/consts/featureFlags';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getFplDataAttributes } from './fplDataAttributes';

interface ButtonContainedProps extends Omit<ButtonProps, 'size'> {
  isFigmaTheme?: boolean;
  size?: 'xs' | 'small' | 'medium' | 'large';
  startIconSize?: number;
  mode?:
    | 'filled-light'
    | 'filled-light-secondary'
    | 'filled-secondary'
    | 'filled-dark'
    | 'filled-destructive'
    | 'outlined'
    | 'outlined-light'
    | 'text';
}

type FplButtonForwardedProps = Omit<
  FplButtonProps,
  'children' | 'iconPrefix' | 'loading' | 'size' | 'variant' | 'width'
>;

const modeToFplVariant = {
  'filled-light': 'primary',
  'filled-light-secondary': 'primary',
  'filled-secondary': 'secondary',
  'filled-dark': 'inverse',
  'filled-destructive': 'destructive',
  outlined: 'secondary',
  'outlined-light': 'secondary',
  text: 'ghost',
} as const satisfies Record<NonNullable<ButtonContainedProps['mode']>, NonNullable<FplButtonProps['variant']>>;

const buttonSizeToFplSize = {
  xs: 'md',
  small: 'md',
  medium: 'lg',
  large: 'lg',
} as const satisfies Record<NonNullable<ButtonContainedProps['size']>, NonNullable<FplButtonProps['size']>>;

const fplStartIconFrameStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flex: '0 0 24px',
  height: 24,
  justifyContent: 'center',
  width: 24,
};

const fplEndIconContentStyle: CSSProperties = {
  alignItems: 'center',
  display: 'inline-flex',
  gap: 4,
  minWidth: 0,
};

const fplEndIconLabelStyle: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const getFplVariant = (
  mode: NonNullable<ButtonContainedProps['mode']>,
  size: NonNullable<ButtonContainedProps['size']>,
): NonNullable<FplButtonProps['variant']> => {
  if (mode === 'text' && size === 'medium') {
    return 'primary';
  }

  return modeToFplVariant[mode];
};

const getBackgroundColor = (mode: ButtonContainedProps['mode'], isFigmaTheme: boolean) => {
  switch (mode) {
    case 'filled-light':
      return isFigmaTheme ? color.White100 : color.Yellow100;
    case 'filled-light-secondary':
      return isFigmaTheme ? color.White100 : color.Yellow_Secondary;
    case 'filled-secondary':
      return color.Black84;
    case 'filled-dark':
      return color.Black92_T;
    case 'filled-destructive':
      return color.Weavy_Error;
    case 'text':
    case 'outlined':
    case 'outlined-light':
      return 'transparent';
    default:
      return isFigmaTheme ? color.White100 : color.Yellow100;
  }
};

const getHoverBackgroundColor = (mode: ButtonContainedProps['mode'], isFigmaTheme: boolean) => {
  switch (mode) {
    case 'filled-light':
      return isFigmaTheme ? color.White80_T : color.Yellow64;
    case 'filled-light-secondary':
      return isFigmaTheme ? color.White80_T : color.Yellow40;
    case 'filled-secondary':
      return color.Black64;
    case 'filled-dark':
      return color.Black84_T;
    case 'filled-destructive':
      return color.Weavy_Error_80_T;
    case 'text':
    case 'outlined':
      return color.White08_T;
    case 'outlined-light':
      return color.Black08_T;
    default:
      return isFigmaTheme ? color.White80_T : color.Yellow64;
  }
};

const getDisabledBackgroundColor = (mode: ButtonContainedProps['mode']) => {
  switch (mode) {
    case 'filled-light':
    case 'filled-secondary':
    case 'filled-light-secondary':
    case 'filled-destructive':
      return color.White04_T;
    case 'filled-dark':
      return color.Black64_T;
    case 'text':
    case 'outlined':
    case 'outlined-light':
      return 'transparent';
    default:
      return color.White04_T;
  }
};

const getTextColor = (mode: ButtonContainedProps['mode'], isFigmaTheme: boolean) => {
  switch (mode) {
    case 'filled-light':
    case 'filled-light-secondary':
      return isFigmaTheme ? color.Black92 : color.Black100;
    case 'filled-destructive':
      return color.Black100;
    case 'text':
    case 'outlined':
      return color.White100;
    case 'outlined-light':
      return color.Black92;
    default:
      return color.White100;
  }
};

const getDisabledTextColor = (mode: ButtonContainedProps['mode']) => {
  switch (mode) {
    case 'text':
    case 'outlined':
      return color.White40_T;
    case 'filled-light':
    case 'filled-light-secondary':
    case 'filled-secondary':
    case 'filled-destructive':
      return color.White16_T;
    case 'outlined-light':
      return color.Black16_T;
    default:
      return color.White100;
  }
};

const getBorder = (mode: ButtonContainedProps['mode']) => {
  switch (mode) {
    case 'outlined':
      return `1px solid ${color.White40_T}`;
    case 'outlined-light':
      return `1px solid ${color.Black16_T}`;
    default:
      return 'none';
  }
};

const getHoverBorder = (mode: ButtonContainedProps['mode']) => {
  switch (mode) {
    case 'outlined':
      return `1px solid ${color.White64_T}`;
    case 'outlined-light':
      return `1px solid ${color.Black64_T}`;
    default:
      return 'none';
  }
};

const getDisabledBorder = (mode: ButtonContainedProps['mode']) => {
  switch (mode) {
    case 'outlined':
      return `1px solid ${color.White08_T}`;
    case 'outlined-light':
      return `1px solid ${color.Black16_T}`;
    default:
      return 'none';
  }
};

const getH = (size: ButtonContainedProps['size']) => {
  switch (size) {
    case 'xs':
      return 24;
    case 'small':
      return 28;
    case 'medium':
      return 32;
    case 'large':
      return 36;
    default:
      return 32;
  }
};

const getFontSize = (size: ButtonContainedProps['size']) => {
  switch (size) {
    case 'xs':
    case 'small':
      return '0.75rem';
    default:
      return '0.875rem';
  }
};

export const ButtonContained = forwardRef<HTMLButtonElement, ButtonContainedProps>(function AppButton(
  {
    mode = 'filled-light',
    children,
    size,
    isFigmaTheme,
    startIconSize,
    startIcon,
    endIcon,
    fullWidth,
    loading,
    ...props
  },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { value: isFigmaThemeEnabled } = useFeatureFlag(FF_FIGMA_THEME);
  // Weave-in-Figma always uses the black/white theme; standalone Weavy keeps the flag.
  const isFigmaEmbed = useIsFigmaEmbedMode();
  const shouldUseFigmaTheme = isFigmaTheme ?? (isFigmaThemeEnabled === true || isFigmaEmbed);

  if (isFplMigrationEnabled) {
    const {
      'aria-controls': ariaControls,
      'aria-current': ariaCurrent,
      'aria-describedby': ariaDescribedBy,
      'aria-disabled': ariaDisabled,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-keyshortcuts': ariaKeyShortcuts,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus,
      className: _className,
      classes: _classes,
      color: _color,
      disabled,
      disableElevation: _disableElevation,
      disableFocusRipple: _disableFocusRipple,
      disableRipple: _disableRipple,
      focusRipple: _focusRipple,
      form,
      formAction,
      formEncType,
      formMethod,
      formNoValidate,
      formTarget,
      id,
      LinkComponent: _LinkComponent,
      name,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onPointerDown,
      role,
      sx: _sx,
      style: _style,
      tabIndex,
      title,
      TouchRippleProps: _TouchRippleProps,
      translate,
      type,
      variant: _variant,
      ..._unsupportedFplButtonProps
    } = props;
    const effectiveFplLoading = loading ?? false;
    const effectiveFplStartIcon =
      !effectiveFplLoading && startIcon ? <span style={fplStartIconFrameStyle}>{startIcon}</span> : undefined;
    const effectiveFplChildren =
      !effectiveFplLoading && endIcon ? (
        <span style={fplEndIconContentStyle}>
          <span style={fplEndIconLabelStyle}>{children}</span>
          {endIcon}
        </span>
      ) : (
        children
      );

    const effectiveSize = size ?? 'medium';
    const forwardedFplButtonProps: FplButtonForwardedProps = {
      ...getFplDataAttributes(props),
      'aria-controls': ariaControls,
      'aria-current': ariaCurrent,
      'aria-describedby': ariaDescribedBy,
      'aria-disabled': ariaDisabled === true || ariaDisabled === 'true' ? true : undefined,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-keyshortcuts': ariaKeyShortcuts,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      autoFocus,
      disabled,
      form,
      formAction,
      formEncType,
      formMethod,
      formNoValidate,
      formTarget,
      id,
      name,
      onBlur,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      onMouseDown,
      onMouseEnter,
      onMouseLeave,
      onPointerDown,
      role,
      tabIndex,
      title,
      translate,
      type,
    };

    return (
      <FplButton
        {...forwardedFplButtonProps}
        ref={ref}
        iconPrefix={effectiveFplStartIcon}
        loading={effectiveFplLoading}
        size={buttonSizeToFplSize[effectiveSize]}
        variant={getFplVariant(mode, effectiveSize)}
        width={fullWidth ? 'fill' : 'hug'}
      >
        {effectiveFplChildren}
      </FplButton>
    );
  }

  return (
    <MuiButton
      size={size === 'xs' ? 'small' : size}
      {...props}
      ref={ref}
      variant="contained"
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      fullWidth={fullWidth}
      loading={loading}
      sx={{
        height: getH(size),
        color: getTextColor(mode, shouldUseFigmaTheme),
        bgcolor: getBackgroundColor(mode, shouldUseFigmaTheme),
        '&:hover': {
          bgcolor: getHoverBackgroundColor(mode, shouldUseFigmaTheme),
          border: getHoverBorder(mode),
        },
        '&.Mui-disabled': {
          bgcolor: getDisabledBackgroundColor(mode),
          color: getDisabledTextColor(mode),
          border: getDisabledBorder(mode),
        },
        '& .MuiButton-startIcon': {
          marginRight: '4px',
          ...(startIconSize != null
            ? {
                '& svg': {
                  width: startIconSize,
                  height: startIconSize,
                },
              }
            : undefined),
        },
        fontSize: getFontSize(size),
        fontWeight: 500,
        border: getBorder(mode),
        ...props.sx,
      }}
    >
      {loading ? <span style={{ opacity: 0 }}>{children}</span> : children}
    </MuiButton>
  );
});
